import { el } from '../utils/dom.js';

/**
 * Tile animation editor — define animation frames for individual tiles.
 * Each frame references a tile from the same tileset + a duration in ms.
 * Animations stored on EditorState as `tileAnimations: Map<globalTileId, [{tileId, duration}]>`.
 */
export class TileAnimationEditor {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
    this._previewRaf = null;
    this._previewFrame = 0;
    this._previewTime = 0;
  }

  show(globalTileId) {
    const ts = this.state.getTilesetForGid(globalTileId);
    if (!ts) return;

    const tileAnimations = this.state.get('tileAnimations') || new Map();
    const existing = tileAnimations.get(globalTileId) || [];
    const frames = existing.map(f => ({ ...f })); // Clone

    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) this._close(overlay);
    }});
    const dialog = el('div', { className: 'dialog', style: { maxWidth: '500px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, `Tile Animation — Tile ${globalTileId}`));

    // Preview canvas
    const previewRow = el('div', { style: { display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' } });
    const previewCanvas = el('canvas', {
      width: ts.tileWidth * 3,
      height: ts.tileHeight * 3,
      style: { border: '1px solid var(--border)', imageRendering: 'pixelated', background: '#1a1a2e' },
    });
    previewRow.appendChild(previewCanvas);

    const previewInfo = el('div', { style: { fontSize: '12px', color: 'var(--text-dim)' } });
    previewInfo.appendChild(el('div', {}, `Source tile: ${globalTileId} (local ${globalTileId - ts.firstGid})`));
    previewInfo.appendChild(el('div', {}, `Tileset: ${ts.name}`));
    previewInfo.appendChild(el('div', { style: { marginTop: '4px' } }, `${frames.length} frame(s)`));
    previewRow.appendChild(previewInfo);
    dialog.appendChild(previewRow);

    // Frame list
    const frameListEl = el('div', { className: 'anim-frame-list', style: { maxHeight: '200px', overflowY: 'auto', marginBottom: '8px' } });
    dialog.appendChild(frameListEl);

    const renderFrameList = () => {
      frameListEl.innerHTML = '';
      if (frames.length === 0) {
        frameListEl.appendChild(el('div', { style: { color: 'var(--text-dim)', fontSize: '12px', padding: '8px' } },
          'No animation frames. Click "Add Frame" to start.'));
        return;
      }
      frames.forEach((frame, i) => {
        const row = el('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid var(--border)' },
        });

        // Frame tile preview
        const miniCanvas = el('canvas', {
          width: ts.tileWidth,
          height: ts.tileHeight,
          style: { imageRendering: 'pixelated', border: '1px solid var(--border)', flexShrink: '0' },
        });
        this._drawTileOnCanvas(miniCanvas, frame.tileId + ts.firstGid, ts);
        row.appendChild(miniCanvas);

        row.appendChild(el('span', { style: { fontSize: '11px', width: '60px' } }, `Tile ${frame.tileId}`));

        // Duration input
        const durInput = el('input', {
          type: 'number',
          className: 'form-input',
          value: String(frame.duration),
          min: '16',
          max: '10000',
          style: { width: '70px', fontSize: '11px' },
        });
        durInput.addEventListener('change', () => {
          frame.duration = parseInt(durInput.value, 10) || 100;
        });
        row.appendChild(durInput);
        row.appendChild(el('span', { style: { fontSize: '11px', color: 'var(--text-dim)' } }, 'ms'));

        // Move up/down
        if (i > 0) {
          row.appendChild(el('button', { className: 'btn btn-sm', onClick: () => {
            [frames[i - 1], frames[i]] = [frames[i], frames[i - 1]];
            renderFrameList();
          }}, '\u2191'));
        }
        if (i < frames.length - 1) {
          row.appendChild(el('button', { className: 'btn btn-sm', onClick: () => {
            [frames[i], frames[i + 1]] = [frames[i + 1], frames[i]];
            renderFrameList();
          }}, '\u2193'));
        }

        // Remove
        row.appendChild(el('button', { className: 'btn btn-sm', style: { color: '#f55' }, onClick: () => {
          frames.splice(i, 1);
          renderFrameList();
        }}, '\u00d7'));

        frameListEl.appendChild(row);
      });
    };
    renderFrameList();

    // Add frame controls
    const addRow = el('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' } });
    const addTileInput = el('input', {
      type: 'number',
      className: 'form-input',
      placeholder: 'Local tile ID',
      min: '0',
      max: String(ts.tileCount - 1),
      style: { width: '100px', fontSize: '11px' },
    });
    const addDurInput = el('input', {
      type: 'number',
      className: 'form-input',
      value: '200',
      min: '16',
      max: '10000',
      style: { width: '70px', fontSize: '11px' },
    });
    const addBtn = el('button', { className: 'btn btn-sm', onClick: () => {
      const tileId = parseInt(addTileInput.value, 10);
      if (isNaN(tileId) || tileId < 0 || tileId >= ts.tileCount) return;
      const duration = parseInt(addDurInput.value, 10) || 200;
      frames.push({ tileId, duration });
      addTileInput.value = '';
      renderFrameList();
    }}, 'Add Frame');

    addRow.append(
      el('span', { style: { fontSize: '11px' } }, 'Tile ID:'), addTileInput,
      el('span', { style: { fontSize: '11px' } }, 'Duration:'), addDurInput,
      addBtn
    );
    dialog.appendChild(addRow);

    // Start preview animation
    this._startPreview(previewCanvas, frames, ts);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const saveBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      if (frames.length > 0) {
        tileAnimations.set(globalTileId, frames);
      } else {
        tileAnimations.delete(globalTileId);
      }
      this.state.set('tileAnimations', tileAnimations);
      this.mapCanvas.invalidateAll();
      this.compositor.requestRender();
      this._close(overlay);
    }}, 'Save');
    const clearBtn = el('button', { className: 'btn', style: { color: '#f55' }, onClick: () => {
      frames.length = 0;
      renderFrameList();
    }}, 'Clear All');
    const cancelBtn = el('button', { className: 'btn', onClick: () => this._close(overlay) }, 'Cancel');
    btnRow.append(saveBtn, clearBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  _startPreview(canvas, frames, ts) {
    const ctx = canvas.getContext('2d');
    const scale = 3;
    let frameIndex = 0;
    let lastTime = performance.now();
    let elapsed = 0;

    const draw = (time) => {
      const dt = time - lastTime;
      lastTime = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (frames.length === 0) {
        // No animation — show nothing
        this._previewRaf = requestAnimationFrame(draw);
        return;
      }

      elapsed += dt;
      const frame = frames[frameIndex];
      if (elapsed >= frame.duration) {
        elapsed -= frame.duration;
        frameIndex = (frameIndex + 1) % frames.length;
      }

      const currentFrame = frames[frameIndex];
      const gid = currentFrame.tileId + ts.firstGid;
      const rect = this.state.getTileRect(gid);
      if (rect) {
        const img = this.state.get('tilesetImages').get(rect.tileset.image);
        if (img) {
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, ts.tileWidth * scale, ts.tileHeight * scale);
        }
      }

      this._previewRaf = requestAnimationFrame(draw);
    };
    this._previewRaf = requestAnimationFrame(draw);
  }

  _drawTileOnCanvas(canvas, gid, ts) {
    const ctx = canvas.getContext('2d');
    const rect = this.state.getTileRect(gid);
    if (!rect) return;
    const img = this.state.get('tilesetImages').get(rect.tileset.image);
    if (!img) return;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, ts.tileWidth, ts.tileHeight);
  }

  _close(overlay) {
    if (this._previewRaf) {
      cancelAnimationFrame(this._previewRaf);
      this._previewRaf = null;
    }
    overlay.remove();
  }
}
